<article>
    <header class="flex min-h-[4.5rem] items-center gap-2 border-b border-slate-100 px-3 py-3">
        <button type="button" title="Torna ai messaggi" aria-label="Torna ai messaggi"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                (click)="previousPage()">
            <i class="bi bi-arrow-left text-lg" aria-hidden="true"></i>
        </button>
        <h1 *ngIf="message; else titleSkeleton" class="m-0 min-w-0 truncate text-base font-bold text-slate-900">{{ message.title }}</h1>
        <ng-template #titleSkeleton><span class="h-4 w-56 animate-pulse rounded bg-slate-200"></span></ng-template>
    </header>

    <div class="px-5 py-5">
        <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <div *ngIf="message && sender; else senderSkeleton" class="flex min-w-0 items-center gap-3">
                <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary" aria-hidden="true">
                    {{ senderInitials }}
                </span>
                <span class="truncate text-sm font-semibold text-slate-900">{{ sender.firstName }} {{ sender.lastName }}</span>
            </div>
            <ng-template #senderSkeleton>
                <div class="flex animate-pulse items-center gap-3">
                    <span class="h-9 w-9 rounded-full bg-slate-200"></span>
                    <span class="h-3 w-32 rounded bg-slate-200"></span>
                </div>
            </ng-template>

            <time *ngIf="message" class="text-xs text-slate-500" [attr.datetime]="message.createdAt">
                {{ formatMessageDate(message.createdAt!) }}<ng-container *ngIf="received(message.createdAt) as ago"> · {{ ago }}</ng-container>
            </time>
        </div>

        <div *ngIf="message; else bodySkeleton"
             [ngClass]="{'min-h-[16rem]': !isSent}" class="mt-5 max-w-3xl text-[0.95rem] leading-relaxed text-slate-700 [overflow-wrap:anywhere] [&_a]:text-primary [&_a]:underline [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-lg [&_ol]:mb-3 [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:pl-5"
             [innerHTML]="messageBody"></div>
        <ng-template #bodySkeleton>
            <div class="mt-5 flex min-h-[16rem] max-w-3xl animate-pulse flex-col gap-2.5" aria-busy="true" aria-label="Caricamento messaggio">
                <span class="h-3 w-full rounded bg-slate-100"></span>
                <span class="h-3 w-11/12 rounded bg-slate-100"></span>
                <span class="h-3 w-4/5 rounded bg-slate-100"></span>
                <span class="h-3 w-2/3 rounded bg-slate-100"></span>
            </div>
        </ng-template>
    </div>

    <section *ngIf="isSent" class="border-t border-slate-100 px-5 py-5" aria-labelledby="recipientsHeading">
        <ng-container *ngIf="recipients; else recipientsSkeleton">
            <div class="flex flex-wrap items-center justify-between gap-2">
                <h2 id="recipientsHeading" class="m-0 text-sm font-bold text-slate-900">Destinatari</h2>
                <span class="inline-flex max-w-full items-center gap-1.5 truncate rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                    <i class="bi" [ngClass]="message?.recipientType === 'user' ? 'bi-person' : message?.recipientType === 'category' ? 'bi-folder2' : 'bi-people'" aria-hidden="true"></i>
                    <span class="truncate">{{ recipients.recipientLabel }}</span>
                </span>
            </div>

            <div class="mt-3">
                <div class="flex items-baseline justify-between text-xs text-slate-500">
                    <span><strong class="text-slate-900">{{ readRecipients.length }}</strong> di {{ recipients.recipients.length }} hanno letto</span>
                    <span>{{ readPercent }}%</span>
                </div>
                <div class="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100" role="progressbar"
                     [attr.aria-valuenow]="readPercent" aria-valuemin="0" aria-valuemax="100" aria-label="Messaggio letto">
                    <div class="h-full rounded-full bg-primary transition-[width]" [style.width.%]="readPercent"></div>
                </div>
            </div>

            <div class="mt-4 inline-flex rounded-lg bg-slate-100 p-0.5" role="group" aria-label="Filtra destinatari">
                <button type="button" class="rounded-md border-0 px-3 py-1 text-xs font-semibold transition-colors"
                        [ngClass]="recipientsFilter === 'unread' ? 'bg-white text-slate-900 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-800'"
                        [attr.aria-pressed]="recipientsFilter === 'unread'" (click)="recipientsFilter = 'unread'">
                    Non letto · {{ unreadRecipients.length }}
                </button>
                <button type="button" class="rounded-md border-0 px-3 py-1 text-xs font-semibold transition-colors"
                        [ngClass]="recipientsFilter === 'read' ? 'bg-white text-slate-900 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-800'"
                        [attr.aria-pressed]="recipientsFilter === 'read'" (click)="recipientsFilter = 'read'">
                    Letto · {{ readRecipients.length }}
                </button>
            </div>

            <ul class="m-0 mt-3 max-h-80 list-none divide-y divide-slate-100 overflow-y-auto p-0">
                <li *ngFor="let recipient of (recipientsFilter === 'read' ? readRecipients : unreadRecipients)"
                    class="flex items-center gap-3 py-2.5">
                    <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[0.7rem] font-bold text-slate-600" aria-hidden="true">
                        {{ initials(recipient.firstName, recipient.lastName) }}
                    </span>
                    <div class="min-w-0 flex-1">
                        <p class="m-0 truncate text-sm font-medium text-slate-900">{{ recipient.firstName }} {{ recipient.lastName }}</p>
                        <p class="m-0 truncate text-xs text-slate-500">{{ recipient.email }}</p>
                    </div>
                    <span *ngIf="recipient.seen; else notRead" class="shrink-0 text-xs text-primary">
                        <i class="bi bi-check2-all" aria-hidden="true"></i> {{ formatMessageDate(recipient.seenAt!) }}
                    </span>
                    <ng-template #notRead><span class="shrink-0 text-xs text-slate-400">Non letto</span></ng-template>
                </li>
                <li *ngIf="!(recipientsFilter === 'read' ? readRecipients : unreadRecipients).length" class="py-4 text-center text-sm text-slate-400">
                    {{ recipientsFilter === 'read' ? 'Nessuno ha ancora letto il messaggio.' : 'Tutti hanno letto il messaggio.' }}
                </li>
            </ul>
        </ng-container>

        <ng-template #recipientsSkeleton>
            <div class="flex animate-pulse flex-col gap-3" aria-busy="true" aria-label="Caricamento destinatari">
                <span class="h-3.5 w-24 rounded bg-slate-200"></span>
                <span class="h-1.5 w-full rounded-full bg-slate-100"></span>
                <span *ngFor="let row of [1, 2, 3]" class="h-8 w-full rounded bg-slate-100"></span>
            </div>
        </ng-template>
    </section>
</article>
